// Offline — mounted once by AppShell, read by any screen.
//
// This used to live in apps/field/shell.js and be mounted only by FieldApp,
// which meant the queue existed for the five Field routes and nowhere else: a
// crew lead who moved a job on the Board with no signal lost the change, and
// the same phone that was safe on /field was not safe on /board. There is one
// app now, so there is one queue, mounted above every route.
//
//   <OfflineProvider>    mounts useOffline once; after a replay lands it
//                        invalidates every 'field' query so screens catch up
//   useOfflineQueue()    { online, pendingCount, failed, replayNow, ... }
//   useResolvedNow(q)    the `now` to show: the server's, or the optimistic
//                        one while a clock op is still queued
//   <OfflineBanner>      the shell's banner slot

import React, { createContext, useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Notice } from '../../ui/fw';
import { useAuth } from '../../App';
import { useOffline } from '../field/offline/useOffline';
import { readOptimisticNow, clearOptimisticNow, resolveNow } from '../field/offline/optimisticNow';
import { plural } from '../field/fmt';

const OfflineContext = createContext(null);

export function OfflineProvider({ children, queue, photos }) {
  const qc = useQueryClient();
  // The queues need to know WHO is signed in before they replay anything. A
  // queued clock-in carries no user id of its own — the server attributes it
  // from whatever bearer token the replay happens to use — so a phone that
  // changes hands writes the first person's hours to the second person's
  // timesheet. See apps/field/offline/owner.js.
  const { user } = useAuth();
  const off = useOffline({
    queue,
    photos,
    owner: user ? user.id : null,
    onReplayed: () => qc.invalidateQueries({ queryKey: ['field'] }),
  });
  return <OfflineContext.Provider value={off}>{children}</OfflineContext.Provider>;
}

export function useOfflineQueue() {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error('useOfflineQueue must be used inside <OfflineProvider>');
  return ctx;
}

/**
 * The `now` a screen should show. Reads the optimistic entry each render
 * (cheap; localStorage) and clears it once the server has caught up.
 */
export function useResolvedNow(todayQuery) {
  const off = useOfflineQueue();
  const data = todayQuery.data;
  const fresh = !!data && !data.__stale;
  const optimistic = readOptimisticNow();
  const { now, clear } = resolveNow(data ? data.now : null, optimistic, off.queue, fresh);
  useEffect(() => { if (clear) clearOptimisticNow(); }, [clear]);
  return now;
}

const OP_LABEL = {
  clock_in: 'Clock in',
  clock_out: 'Clock out',
  subtask: 'Checklist item',
  note: 'Note',
  problem: 'Problem report',
  photo: 'Photo',
};

export function OfflineBanner() {
  const { online, pendingCount, failed, dismissFailed } = useOfflineQueue();
  const show = !online || pendingCount > 0 || failed.length > 0;
  if (!show) return null;
  return (
    <div className="fd-banners">
      {(!online || pendingCount > 0) ? (
        <Notice tone="warn" icon="wifiOff" title={online ? 'Sending' : 'No signal'}>
          {pendingCount > 0
            ? `Saved on this phone — ${plural(pendingCount, 'item')} will send when you have signal. Nothing is lost.`
            : 'What you do here is saved on this phone and sent when you have signal. Nothing is lost.'}
        </Notice>
      ) : null}
      {failed.map((f) => (
        <Notice key={f.key} tone="stop" onDismiss={() => dismissFailed(f.key)}>
          1 item could not be sent: {f.error}{f.op && OP_LABEL[f.op] ? ` (${OP_LABEL[f.op]})` : ''}
        </Notice>
      ))}
    </div>
  );
}
